/**
 * The reducer. `(state, action) → state`. Every mutation of the world goes
 * through `step`; nothing else in `/sim` is allowed to be an entry point
 * for change.
 *
 * `step` returns the new state and a report of what happened — what it
 * cost, what was written, what was dropped and why. `reduce` is the same
 * thing with only the state, for callers that want the pure signature.
 *
 * Refused actions return the state unchanged and say why. The sim never
 * throws at a bad action any more than at a bad outcome (§14).
 *
 * Time only moves inside `passTime`, which is also where the two things
 * that resolve "while you weren't looking" happen: appointments lapse and
 * debts come due (§9 clocks), and faded memories are pruned when a day
 * turns (§11). The world does not run; it resolves (§10).
 */

import type { Action, StepReport } from './actions.js';
import { conversationMode } from '../engine/conversation.js';
import { commitEvent } from './events.js';
import { pointInPolygon, pushOutOfBBox } from './geometry.js';
import { actorRef } from './ids.js';
import { type EventDraft, isEventType } from '../engine/memory/events.js';
import { settleObligation } from '../engine/memory/facts.js';
import { formImpression, tagsForEvent } from '../engine/memory/impressions.js';
import { pruneFaded } from '../engine/memory/impressions.js';
import { addTexture } from '../engine/memory/texture.js';
import { closedBy } from './injury.js';
import { effectiveStats } from './needs.js';
import { applyOutcomes } from './outcomes.js';
import { isPresent, npcsAt, whereIs } from './presence.js';
import { Rng } from './rng.js';
import { addMinutes, crossesDay, dayOfWeek, isValidDuration, MINUTES_PER_DAY, minutesBetween, toMinutes } from './time.js';
import { EMPTY_SCENE_BUDGET } from './types/outcome.js';
import { resolveBeat, startScene } from './violence.js';
import { TUNING } from './tuning.js';
import type {
  Actor,
  Appointment,
  EventId,
  LocationId,
  MapPoint,
  Minutes,
  NPC,
  NpcId,
  Outcome,
  TranscriptLine,
  WorldState,
  WorldTime,
} from './types/index.js';
import { actorExists, refExists } from './validate.js';

export interface StepResult {
  readonly state: WorldState;
  readonly report: StepReport;
}

/** The pure signature. */
export function reduce(state: WorldState, action: Action): WorldState {
  return step(state, action).state;
}

/** Apply a sequence, collecting every report. */
export function run(state: WorldState, actions: readonly Action[]): { state: WorldState; reports: StepReport[] } {
  const reports: StepReport[] = [];
  let next = state;
  for (const action of actions) {
    const result = step(next, action);
    next = result.state;
    reports.push(result.report);
  }
  return { state: next, reports };
}

export function step(state: WorldState, action: Action): StepResult {
  const outcome = dispatch(state, action);
  if (typeof outcome === 'string') return refuse(state, action, outcome);
  return outcome;
}

// ---------------------------------------------------------------------------
// Plumbing
// ---------------------------------------------------------------------------

/** Accumulates what a step did while its state threads through helpers. */
interface Ledger {
  readonly events: EventId[];
  readonly notes: string[];
}

function ledger(): Ledger {
  return { events: [], notes: [] };
}

function refuse(state: WorldState, action: Action, reason: string): StepResult {
  return {
    state,
    report: { action, ok: false, refused: reason, elapsed: 0, events: [], applied: [], dropped: [], notes: [] },
  };
}

function done(
  state: WorldState,
  action: Action,
  elapsed: Minutes,
  led: Ledger,
  extra: Partial<Pick<StepReport, 'applied' | 'dropped' | 'beat'>> = {},
): StepResult {
  return {
    state,
    report: {
      action,
      ok: true,
      refused: null,
      elapsed,
      events: led.events,
      applied: extra.applied ?? [],
      dropped: extra.dropped ?? [],
      notes: led.notes,
      ...(extra.beat ? { beat: extra.beat } : {}),
    },
  };
}

function dispatch(state: WorldState, action: Action): StepResult | string {
  if (typeof action !== 'object' || action === null) return 'not an action';
  if (state.player.status !== 'alive') return 'the run is over; the character is finished';
  // A violent scene is the whole world until it's over (§17).
  if (state.scene !== null && action.type !== 'beat') return 'not now';
  switch (action.type) {
    case 'wait':
      return wait(state, action);
    case 'travel':
      return travel(state, action);
    case 'eat':
      return eat(state, action);
    case 'sleep':
      return sleep(state, action);
    case 'talk':
      return talk(state, action);
    case 'scene':
      return scene(state, action);
    case 'open_scene':
      return openScene(state, action);
    case 'leave_scene':
      return leaveScene(state, action);
    case 'event':
      return event(state, action);
    case 'settle_obligation':
      return settle(state, action);
    case 'pay':
      return pay(state, action);
    case 'work':
      return work(state, action);
    case 'move':
      return move(state, action);
    case 'start_scene':
      return start(state, action);
    case 'beat':
      return beat(state, action);
    case 'digest':
      return digest(state, action);
    default:
      return `unknown action type: ${String((action as { type?: unknown }).type)}`;
  }
}

// ---------------------------------------------------------------------------
// Time (§9)
// ---------------------------------------------------------------------------

/**
 * Move the clock and resolve what the passage of time settles. Exported
 * for tests; actions call it, nothing else should.
 */
export function passTime(state: WorldState, minutes: Minutes, led: Ledger = ledger()): WorldState {
  if (minutes <= 0) return state;
  const from = state.time;
  const to = addMinutes(from, minutes);
  let next: WorldState = { ...state, time: to };

  next = lapseAppointments(next, from, to, led);
  next = lapseObligations(next, from, to, led);
  next = resolveOpening(next, from, to, led);
  if (crossesDay(from, to)) next = pruneFaded(next, to);
  return next;
}

/** True when `moment` falls in the half-open window (from, to]. */
function crossed(from: WorldTime, to: WorldTime, moment: WorldTime): boolean {
  const m = toMinutes(moment);
  return m > toMinutes(from) && m <= toMinutes(to);
}

function otherParty(participants: readonly Actor[], not: Actor): Actor | null {
  return participants.find((p) => p !== not) ?? null;
}

function lapseAppointments(state: WorldState, from: WorldTime, to: WorldTime, led: Ledger): WorldState {
  let next = state;
  for (const appt of Object.values(state.appointments)) {
    if (appt.status.kind !== 'pending') continue;
    const deadline = addMinutes(appt.time, TUNING.appointments.graceMinutes);
    if (!crossed(from, to, deadline)) continue;

    // Whoever isn't where they said they'd be. The player's whereabouts are
    // known; NPC no-shows are a V1 situation (§15, §20), so if the player
    // was there the NPC is the one who didn't come.
    const playerIn = appt.participants.includes('player');
    const playerThere = next.player.position.at === appt.place;
    const misser: Actor =
      playerIn && !playerThere ? 'player' : (appt.participants.find((p) => p !== 'player') ?? 'player');
    const stoodUp = otherParty(appt.participants, misser);

    const missed: Appointment = { ...appt, status: { kind: 'missed', by: misser } };
    next = { ...next, appointments: { ...next.appointments, [appt.id]: missed } };
    const committed = commitEvent(
      next,
      {
        type: 'appointment_missed',
        actor: misser,
        target: stoodUp === null ? null : actorRef(stoodUp),
        location: appt.place,
        witnesses: appt.participants.filter((p) => p !== misser && p !== stoodUp),
        outcome: 'failure',
      },
      deadline,
    );
    next = committed.state;
    led.events.push(committed.event.id);
    led.notes.push(`${misser} missed ${appt.id} at ${appt.place}`);
  }
  return next;
}

function lapseObligations(state: WorldState, from: WorldTime, to: WorldTime, led: Ledger): WorldState {
  let next = state;
  for (const fact of Object.values(state.memory.facts)) {
    if (fact.kind !== 'obligation' || fact.settled || fact.deadline === null) continue;
    if (!crossed(from, to, fact.deadline)) continue;
    const committed = commitEvent(
      next,
      {
        type: 'obligation_lapsed',
        actor: fact.from,
        target: actorRef(fact.to),
        location: null,
        witnesses: [],
        outcome: 'failure',
      },
      fact.deadline,
    );
    next = committed.state;
    led.events.push(committed.event.id);
    led.notes.push(`${fact.from} lapsed on ${fact.substance.kind === 'money' ? `$${fact.substance.amount}` : fact.substance.description} owed to ${fact.to}`);
  }
  return next;
}

/**
 * The cold open's deadline, read at the deadline and nowhere upstream (§6).
 * `'none'` is unwinnable regardless of the money: the debt can be settled in
 * full — `pay()` already (correctly) marks the situation `'resolved'` on the
 * spot — and this still overrides that to `'failed'` once the clock runs
 * out, because success/failure for `'none'` is only knowable here. For
 * `'clean'`/`'dirty'`, paying in time is a genuine win and this never fires;
 * left unpaid, it fires the same way `'none'` always does — the "failure
 * must open something" rule (§6) reads as general, not specific to the
 * unwinnable case, so an unpaid `dirty` opening gets the same `failureOpens`
 * inroad an unpaid `none` one does. `obligation_lapsed` (above) still fires
 * on its own, independent, generic terms for any unsettled money obligation
 * at its deadline — this is a second, opening-specific event layered on top,
 * not a replacement for it.
 */
function resolveOpening(state: WorldState, from: WorldTime, to: WorldTime, led: Ledger): WorldState {
  const opening = state.opening;
  if (opening === null) return state;
  if (!crossed(from, to, opening.deadline)) return state;

  const fact = state.memory.facts[opening.obligation];
  const settledInTime = fact !== undefined && fact.kind === 'obligation' && fact.settled;
  if (opening.solvability !== 'none' && settledInTime) return state;

  const situation = state.situations[opening.situation];
  if (!situation || situation.state === 'failed') return state;

  let next: WorldState = {
    ...state,
    situations: { ...state.situations, [situation.id]: { ...situation, state: 'failed' } },
  };
  const committed = commitEvent(
    next,
    {
      type: 'opening_failed',
      actor: opening.dependent,
      target: actorRef(opening.failureOpens),
      location: null,
      witnesses: [],
      outcome: 'failure',
    },
    opening.deadline,
  );
  next = committed.state;
  led.events.push(committed.event.id);
  led.notes.push(`the opening problem went bad: ${opening.failureOpens} is in play now`);
  return next;
}

// ---------------------------------------------------------------------------
// wait
// ---------------------------------------------------------------------------

function wait(state: WorldState, action: Extract<Action, { type: 'wait' }>): StepResult | string {
  if (!isValidDuration(action.minutes)) return 'minutes must be a positive whole number';
  const led = ledger();
  return done(passTime(state, action.minutes, led), action, action.minutes, led);
}

// ---------------------------------------------------------------------------
// travel (§16)
// ---------------------------------------------------------------------------

function travel(state: WorldState, action: Extract<Action, { type: 'travel' }>): StepResult | string {
  const destination = state.locations[action.to];
  if (!destination) return 'no such place';
  if (!state.player.knownLocations.includes(action.to)) return "you don't know where that is";
  if (state.player.position.at === action.to) return 'already there';

  const { point } = state.player.position;
  const distance = Math.hypot(destination.entrance.x - point.x, destination.entrance.y - point.y);
  const minutes = Math.max(
    TUNING.travel.minimumMinutes,
    Math.ceil(distance * TUNING.travel.minutesPerMapUnit),
  );

  const led = ledger();
  // In transit you are at no door; anything due while you walk, you miss.
  let next: WorldState = { ...state, player: { ...state.player, position: { point, at: null, debt: 0 } } };
  next = passTime(next, minutes, led);
  next = {
    ...next,
    player: { ...next.player, position: { point: destination.entrance, at: action.to, debt: 0 } },
    locations: {
      ...next.locations,
      [action.to]: { ...destination, lastResolved: next.time },
    },
  };
  return done(next, action, minutes, led);
}

// ---------------------------------------------------------------------------
// eat / sleep (§9 the needs floor)
// ---------------------------------------------------------------------------

const SELLS_FOOD: ReadonlySet<string> = new Set(['diner', 'bar', 'shop']);

function eat(state: WorldState, action: Extract<Action, { type: 'eat' }>): StepResult | string {
  const here = state.player.position.at;
  const location = here === null ? undefined : state.locations[here];
  if (!location) return 'you are not anywhere you can eat';
  if (!SELLS_FOOD.has(location.kind)) return `nothing to eat at ${location.name}`;

  const price = Math.round(TUNING.needs.mealPrice * state.economy.priceIndex * 100) / 100;
  if (state.player.money < price) return `a meal is $${price.toFixed(2)} and you have $${state.player.money.toFixed(2)}`;

  const led = ledger();
  const minutes = TUNING.needs.mealMinutes;
  let next = passTime(state, minutes, led);
  next = {
    ...next,
    player: {
      ...next.player,
      money: Math.round((next.player.money - price) * 100) / 100,
      needs: { ...next.player.needs, lastAte: next.time },
    },
  };
  led.notes.push(`ate at ${location.name} for $${price.toFixed(2)}`);
  return done(next, action, minutes, led);
}

function sleep(state: WorldState, action: Extract<Action, { type: 'sleep' }>): StepResult | string {
  if (!isValidDuration(action.minutes)) return 'minutes must be a positive whole number';
  const { lodging, position } = state.player;
  const inBed = lodging !== null && position.at === lodging;

  const led = ledger();
  // A real player has no control over how well they sleep on a stoop: the
  // requested minutes are the *bed* case only. Rough sleep draws its own
  // duration from the sim's seeded RNG, forked off a label keyed to the
  // current time so it doesn't collide with another rough sleep later in
  // the run and doesn't disturb any other draw sequence (see violence.ts's
  // `scene:` fork for the same pattern).
  let minutes = action.minutes;
  let rngState = state.rng;
  if (!inBed) {
    const rng = Rng.fromState(state.rng, state.seed).fork(`sleep:${toMinutes(state.time)}`);
    minutes = rng.intInclusive(TUNING.needs.roughSleepMinMinutes, TUNING.needs.roughSleepMaxMinutes);
    rngState = rng.getState();
  }

  const next = passTime({ ...state, rng: rngState }, minutes, led);
  const wake = next.time;
  // Rough sleep is sleep, but you wake already worn. Homelessness is a
  // situation, not a resting state (§9).
  const lastSlept = inBed ? wake : addMinutes(wake, -TUNING.needs.roughSleepPenalty);
  led.notes.push(inBed ? 'slept in your own bed' : 'slept rough');
  return done(
    { ...next, player: { ...next.player, needs: { ...next.player.needs, lastSlept } } },
    action,
    minutes,
    led,
  );
}

// ---------------------------------------------------------------------------
// talk / scene (§14)
// ---------------------------------------------------------------------------

/** §12: confidence = f(NPC opacity, time spent together). */
export function readConfidence(npc: NPC, exposure: number): number {
  const familiarity = Math.min(1, exposure / TUNING.read.fullConfidenceExposure);
  const clarity = 1 - npc.deceptionCapacity * TUNING.read.opacityWeight;
  return Math.round(familiarity * clarity * 1000) / 1000;
}

interface Encounter {
  readonly state: WorldState;
  readonly npc: NPC;
  readonly here: LocationId;
  readonly sceneEvent: EventId;
}

/**
 * What talking and a scene share: you must be at the door and he must be
 * behind it (§16); the meeting is on the record; you know him a little
 * better; any appointment this fulfils is kept.
 */
function meet(state: WorldState, npcId: NpcId, minutes: Minutes, led: Ledger): Encounter | string {
  if (!isValidDuration(minutes)) return 'minutes must be a positive whole number';
  const npc = state.npcs[npcId];
  if (!npc) return 'no such person';
  const here = state.player.position.at;
  if (here === null) return 'you are not at a door';
  if (!isPresent(state, npcId)) {
    const where = whereIs(npc, state.time);
    return where === null ? `${npc.name} is nowhere you can reach right now` : `${npc.name} is not here`;
  }

  const exposure = npc.read.exposure + minutes;
  const known: NPC = {
    ...npc,
    lastResolved: state.time,
    read: {
      ...npc.read,
      exposure,
      confidence: readConfidence(npc, exposure),
      acquaintance: 'known',
      lastSeen: { at: state.time, where: here },
    },
  };
  let next: WorldState = { ...state, npcs: { ...state.npcs, [npcId]: known } };

  const witnesses = npcsAt(next, here).filter((id) => id !== npcId);
  const committed = commitEvent(next, {
    type: 'conversation',
    actor: 'player',
    target: { kind: 'npc', id: npcId },
    location: here,
    witnesses,
    outcome: null,
  });
  next = committed.state;
  led.events.push(committed.event.id);

  next = keepAppointments(next, npcId, here, led);
  return { state: next, npc: known, here, sceneEvent: committed.event.id };
}

function keepAppointments(state: WorldState, npcId: NpcId, here: LocationId, led: Ledger): WorldState {
  let next = state;
  const grace = TUNING.appointments.graceMinutes;
  for (const appt of Object.values(state.appointments)) {
    if (appt.status.kind !== 'pending' || appt.place !== here) continue;
    if (!appt.participants.includes('player') || !appt.participants.includes(npcId)) continue;
    if (Math.abs(minutesBetween(appt.time, next.time)) > grace) continue;

    const kept: Appointment = { ...appt, status: { kind: 'kept', on: next.time } };
    next = { ...next, appointments: { ...next.appointments, [appt.id]: kept } };
    const committed = commitEvent(next, {
      type: 'appointment_kept',
      actor: 'player',
      target: { kind: 'npc', id: npcId },
      location: here,
      witnesses: [],
      outcome: 'success',
    });
    next = committed.state;
    led.events.push(committed.event.id);
    led.notes.push(`kept ${appt.id}`);
  }
  return next;
}

function talk(state: WorldState, action: Extract<Action, { type: 'talk' }>): StepResult | string {
  const led = ledger();
  const mode = conversationMode(state, action.npc);
  const met = meet(state, action.npc, action.minutes, led);
  if (typeof met === 'string') return met;
  led.notes.push(`mode: ${mode}`);
  const next = passTime(met.state, action.minutes, led);
  // With the model off, what he says is his card's line for his mood. The
  // sim picks it so the UI never has to look at the true layer.
  const said = { npc: met.npc.id, line: met.npc.voice.examples[met.npc.truth.mood.mood] };
  return { ...done(next, action, action.minutes, led), report: { ...done(next, action, action.minutes, led).report, said } };
}

function scene(state: WorldState, action: Extract<Action, { type: 'scene' }>): StepResult | string {
  const { contract } = action;
  if (typeof contract !== 'object' || contract === null || !Array.isArray(contract.outcomes)) {
    return 'malformed contract';
  }
  const led = ledger();
  const met = meet(state, action.npc, action.minutes, led);
  if (typeof met === 'string') return met;

  // §14: the per-scene relationship/stance cap is spent once per scene, not
  // once per exchange. A scene is one continuous conversation with one
  // counterpart, so a carried conversation only applies if it belongs to
  // this same NPC; a different counterpart (or no scene in progress) starts
  // fresh. `scene_end` — not this call returning — is what closes it out.
  const carried = met.state.activeConversation;
  const carriedHere = carried !== null && carried.npc === action.npc ? carried : null;
  const incomingBudget = carriedHere?.budget ?? EMPTY_SCENE_BUDGET;

  const application = applyOutcomes(met.state, contract.outcomes, {
    sceneEvent: met.sceneEvent,
    counterpart: action.npc,
    budget: incomingBudget,
  });
  let next = application.state;
  led.events.push(...application.events);

  // The prose still stands, whatever was dropped (§14). It goes to the one
  // lossy layer and nowhere else: generated text never becomes state (§1).
  const prose = typeof contract.prose === 'string' ? contract.prose.trim() : '';
  if (prose !== '') {
    next = addTexture(next, prose, [{ kind: 'player' }, { kind: 'npc', id: action.npc }]).state;
  }

  // The transcript mirrors exactly what the model has already seen as prior
  // turns — nothing else joins it. Same trim-and-skip-if-empty treatment as
  // the texture layer gets, above.
  const playerLine = typeof action.playerText === 'string' ? action.playerText.trim() : '';
  const transcript: TranscriptLine[] = [...(carriedHere?.transcript ?? [])];
  if (playerLine !== '') transcript.push({ who: 'player', text: playerLine });
  if (prose !== '') transcript.push({ who: 'npc', text: prose });

  next = {
    ...next,
    activeConversation:
      contract.scene_end === true ? null : { npc: action.npc, budget: application.budget, transcript },
  };

  next = passTime(next, action.minutes, led);
  return done(next, action, action.minutes, led, {
    applied: application.applied,
    dropped: application.dropped,
  });
}

/**
 * He speaks first (§14): before the player has said anything, the NPC's
 * opening line seeds a fresh conversation. Free — no `meet()`, no time, no
 * outcomes — the same shape `digest()` uses for a costless, stateful action.
 * Always starts clean, whether or not one was already open (with this NPC
 * or another): opening is the start of a conversation, not a resume.
 */
function openScene(state: WorldState, action: Extract<Action, { type: 'open_scene' }>): StepResult | string {
  if (!state.npcs[action.npc]) return 'no such person';
  const line = typeof action.line === 'string' ? action.line.trim() : '';
  const transcript: TranscriptLine[] = line !== '' ? [{ who: 'npc', text: line }] : [];
  const next: WorldState = {
    ...state,
    activeConversation: { npc: action.npc, budget: EMPTY_SCENE_BUDGET, transcript },
  };
  return done(next, action, 0, ledger());
}

/**
 * Walk away (§16): closes whatever conversation is open with nothing more
 * than the fact of leaving. Free, like `openScene` — nothing happened that
 * costs time or produces an outcome, so there's nothing for `meet()` to do.
 * The transcript is discarded (§14: only the contract persists, and this
 * exit isn't one); anything applyOutcomes already committed from an earlier
 * exchange in the scene is untouched by this.
 */
function leaveScene(state: WorldState, action: Extract<Action, { type: 'leave_scene' }>): StepResult | string {
  if (state.activeConversation === null) return 'no conversation to leave';
  const next: WorldState = { ...state, activeConversation: null };
  return done(next, action, 0, ledger());
}

function digest(state: WorldState, action: Extract<Action, { type: 'digest' }>): StepResult | string {
  if (!Number.isInteger(action.day) || action.day >= state.time.day) return 'a digest is for a day that is over';
  const prose = typeof action.prose === 'string' ? action.prose.trim() : '';
  if (prose === '') return 'nothing to tell';
  const led = ledger();
  // Generated text never becomes state (§1): texture only, about the player.
  const next = addTexture(state, prose, [{ kind: 'player' }], { day: action.day, minute: MINUTES_PER_DAY - 1 }).state;
  led.notes.push(`digest for day ${action.day}`);
  return done(next, action, 0, led);
}

// ---------------------------------------------------------------------------
// event — the hook for machinery to come
// ---------------------------------------------------------------------------

const EVENT_OUTCOMES: ReadonlySet<unknown> = new Set(['success', 'failure', null]);

function event(state: WorldState, action: Extract<Action, { type: 'event' }>): StepResult | string {
  const draft = action.event;
  if (typeof draft !== 'object' || draft === null) return 'no event';
  if (!isEventType(draft.type)) return `unknown event type: ${String(draft.type)}`;
  if (!actorExists(state, draft.actor)) return 'actor does not exist';
  if (draft.target !== null && !refExists(state, draft.target)) return 'target does not exist';
  if (draft.location !== null && !(draft.location in state.locations)) return 'location does not exist';
  if (!Array.isArray(draft.witnesses)) return 'witnesses must be a list';
  if (!EVENT_OUTCOMES.has(draft.outcome)) return 'outcome must be success, failure or null';
  if (action.minutes !== undefined && !isValidDuration(action.minutes)) {
    return 'minutes must be a positive whole number';
  }

  const led = ledger();
  const witnesses = draft.witnesses.filter((w) => actorExists(state, w));
  if (witnesses.length !== draft.witnesses.length) led.notes.push('ignored witnesses who do not exist');
  const clean: EventDraft = { ...draft, witnesses };

  const committed = commitEvent(state, clean);
  led.events.push(committed.event.id);
  for (const imp of committed.impressions) {
    led.notes.push(`${imp.npc} remembers it (${imp.permanence.tier})`);
  }
  const minutes = action.minutes ?? 0;
  const next = passTime(committed.state, minutes, led);
  return done(next, action, minutes, led);
}

// ---------------------------------------------------------------------------
// settle_obligation
// ---------------------------------------------------------------------------

function settle(state: WorldState, action: Extract<Action, { type: 'settle_obligation' }>): StepResult | string {
  const fact = state.memory.facts[action.fact];
  if (fact?.kind === 'obligation' && fact.substance.kind === 'money') return 'money is paid, not settled';
  const next = settleObligation(state, action.fact);
  if (next === null) return 'no such open obligation';
  return done(next, action, 0, ledger());
}

// ---------------------------------------------------------------------------
// pay — cash to the creditor in front of you
// ---------------------------------------------------------------------------

function pay(state: WorldState, action: Extract<Action, { type: 'pay' }>): StepResult | string {
  const fact = state.memory.facts[action.fact];
  if (!fact || fact.kind !== 'obligation' || fact.settled) return 'no such open debt';
  if (fact.substance.kind !== 'money') return 'that is not money owed';
  if (fact.to === 'player') return 'that is owed to you';
  const creditor = state.npcs[fact.to];
  if (!creditor) return 'the creditor does not exist';
  if (!isPresent(state, fact.to)) return `${creditor.name} is not here to take it`;
  const amount = fact.substance.amount;
  if (state.player.money < amount) return `you have $${state.player.money.toFixed(2)} and it's $${amount}`;
  const here = state.player.position.at!;

  const led = ledger();
  let next = settleObligation(state, fact.id)!;
  next = {
    ...next,
    player: { ...next.player, money: Math.round((next.player.money - amount) * 100) / 100 },
    npcs: { ...next.npcs, [creditor.id]: { ...creditor, truth: { ...creditor.truth, money: creditor.truth.money + amount } } },
  };
  const committed = commitEvent(next, {
    type: 'transfer',
    actor: 'player',
    target: { kind: 'npc', id: creditor.id },
    location: here,
    witnesses: npcsAt(next, here).filter((id) => id !== creditor.id),
    outcome: 'success',
  });
  next = committed.state;
  led.events.push(committed.event.id);

  // The creditor: paid in full, on time or not, is worth something to him.
  const outcomes: Outcome[] = [
    { type: 'relationship_shift', target: creditor.id, visible: true, source: 'sim', payload: { trust: 4, respect: 3 } },
  ];
  next = applyOutcomes(next, outcomes, { sceneEvent: committed.event.id, counterpart: creditor.id }).state;

  // Paying someone else's debt is significant help when they were exposed
  // (§11): categorical, permanent, theirs to keep.
  if (fact.from !== 'player' && next.npcs[fact.from]) {
    const formed = formImpression(next, fact.from, {
      type: 'transfer',
      actor: 'player',
      target: { kind: 'npc', id: creditor.id },
      location: here,
      at: next.time,
    }, {
      origin: committed.event.id,
      source: { kind: 'told', by: creditor.id },
      trigger: 'help_when_exposed',
      tags: [...tagsForEvent(committed.event), 'debt'],
    });
    next = formed.state;
    next = applyOutcomes(next, [
      { type: 'relationship_shift', target: fact.from, visible: true, source: 'sim', payload: { trust: 8, warmth: 8 } },
    ], { sceneEvent: committed.event.id, counterpart: fact.from }).state;
  }

  // The cold open closes when its debt does (§6). A `'none'` opening (or a
  // `'clean'`/`'dirty'` one paid too late) may already have been failed by
  // `resolveOpening` at the deadline (above, called from `passTime`) — the
  // deadline has the final word, so a payment arriving after it does not
  // resurrect an already-failed situation.
  if (next.opening && next.opening.obligation === fact.id) {
    const situation = next.situations[next.opening.situation];
    if (situation && situation.state !== 'failed') {
      next = { ...next, situations: { ...next.situations, [situation.id]: { ...situation, state: 'resolved' } } };
    }
    led.notes.push('the opening problem is settled');
  }
  led.notes.push(`paid ${creditor.name} $${amount}${fact.from === 'player' ? '' : ` for ${next.npcs[fact.from]?.name ?? fact.from}`}`);
  return done(next, action, 0, led);
}

// ---------------------------------------------------------------------------
// work (§7, §20)
// ---------------------------------------------------------------------------

const STAT_NAMES = ['nerve', 'read', 'presence', 'body'] as const;

function work(state: WorldState, action: Extract<Action, { type: 'work' }>): StepResult | string {
  const job = state.jobs[action.job];
  if (!job) return 'no such job';
  const here = state.player.position.at;
  if (here !== job.at) return `${job.name} is at ${state.locations[job.at]?.name ?? job.at}, not here`;
  const employer = state.npcs[job.employer];
  if (!employer) return 'the employer does not exist';
  if (!isPresent(state, job.employer)) return `${employer.name} is not here to put you to work`;
  if (!job.days.includes(dayOfWeek(state.time))) return `${job.name} does not run today`;
  if (state.time.minute < job.window.from || state.time.minute >= job.window.to) {
    return `too ${state.time.minute < job.window.from ? 'early' : 'late'} for ${job.name}`;
  }
  if (employer.truth.relationship.trust < job.requires.trust) return `${employer.name} does not trust you with it`;
  const stats = effectiveStats(state);
  for (const stat of STAT_NAMES) {
    const needed = job.requires.stats[stat];
    if (needed !== undefined && stats[stat] < needed) return `you don't have the ${stat} for it today`;
  }
  if (employer.truth.money < job.pay) return `${employer.name} cannot pay you today`;
  const closed = closedBy(state.player.injuries, 'work', state.time);
  if (closed) return `you can't work with a ${closed.site}`;

  const led = ledger();
  let next = passTime(state, job.minutes, led);
  // Showing up and doing the work is the quiet, continuous kind of
  // "liked that" (§11): a nudge, never a notification.
  const respect = Math.min(TUNING.relationship.max, employer.truth.relationship.respect + 1);
  const paid: NPC = {
    ...employer,
    truth: { ...employer.truth, money: employer.truth.money - job.pay, relationship: { ...employer.truth.relationship, respect } },
    read: { ...employer.read, relationship: { ...employer.read.relationship, respect: Math.min(TUNING.relationship.max, employer.read.relationship.respect + 1) } },
  };
  next = {
    ...next,
    npcs: { ...next.npcs, [employer.id]: paid },
    player: { ...next.player, money: Math.round((next.player.money + job.pay) * 100) / 100 },
  };
  const committed = commitEvent(next, {
    type: 'transfer',
    actor: employer.id,
    target: { kind: 'player' },
    location: here,
    witnesses: npcsAt(next, here).filter((id) => id !== employer.id),
    outcome: 'success',
  });
  next = committed.state;
  led.events.push(committed.event.id);
  led.notes.push(`worked ${job.name} for $${job.pay}${job.kind === 'illegal' ? ' (off the books)' : ''}`);
  return done(next, action, job.minutes, led);
}

// ---------------------------------------------------------------------------
// move (§16) — the joystick and the tap on open ground
// ---------------------------------------------------------------------------

/** How close to an entrance counts as standing at the door, in map units. */
export const DOOR_RADIUS = 0.35;
/** The farthest one `move` may carry you. A joystick nudges; it does not teleport. */
export const MAX_MOVE = 1.5;

/** The location whose door this point is at, or null. Nearest wins if two overlap. */
export function doorAt(state: WorldState, point: { readonly x: number; readonly y: number }): LocationId | null {
  let best: LocationId | null = null;
  let bestDistance = DOOR_RADIUS;
  for (const location of Object.values(state.locations)) {
    const d = Math.hypot(location.entrance.x - point.x, location.entrance.y - point.y);
    if (d <= bestDistance) {
      best = location.id;
      bestDistance = d;
    }
  }
  return best;
}

/**
 * Push `point` out of any building or filler footprint it lands inside,
 * except the location `at` (the door you've just arrived at — reaching your
 * own destination necessarily means standing inside its own rectangle).
 * Filler is never excluded: nothing is ever "arriving at" decoration. One
 * bbox push-out per offending footprint, in encounter order — sufficient for
 * V0's non-overlapping rectangles; no sliding, no physics.
 */
function resolveCollision(state: WorldState, point: MapPoint, at: LocationId | null): MapPoint {
  let settled = point;
  for (const building of state.footprints.buildings) {
    if (building.location === at) continue;
    if (pointInPolygon(settled, building.vertices)) settled = pushOutOfBBox(settled, building.bbox);
  }
  for (const filler of state.footprints.filler) {
    if (pointInPolygon(settled, filler.vertices)) settled = pushOutOfBBox(settled, filler.bbox);
  }
  return settled;
}

function move(state: WorldState, action: Extract<Action, { type: 'move' }>): StepResult | string {
  const to = action.to;
  if (typeof to !== 'object' || to === null || !Number.isFinite(to.x) || !Number.isFinite(to.y)) return 'not a point';
  const b = state.bounds;
  const target = {
    x: Math.min(b.x + b.width, Math.max(b.x, to.x)),
    y: Math.min(b.y + b.height, Math.max(b.y, to.y)),
  };
  const from = state.player.position.point;
  let dx = target.x - from.x;
  let dy = target.y - from.y;
  let distance = Math.hypot(dx, dy);
  if (distance === 0) return 'already there';
  if (distance > MAX_MOVE) {
    dx *= MAX_MOVE / distance;
    dy *= MAX_MOVE / distance;
    distance = MAX_MOVE;
  }
  const point = { x: from.x + dx, y: from.y + dy };

  // Charge whole minutes; carry the fraction (§9).
  const owed = state.player.position.debt + distance * TUNING.travel.minutesPerMapUnit;
  const minutes = Math.floor(owed);
  const debt = owed - minutes;

  const led = ledger();
  let next: WorldState = { ...state, player: { ...state.player, position: { point: from, at: null, debt } } };
  next = passTime(next, minutes, led);
  const at = doorAt(next, point);
  const settled = resolveCollision(next, point, at);
  next = { ...next, player: { ...next.player, position: { point: settled, at, debt } } };
  if (at !== null && at !== state.player.position.at) {
    const location = next.locations[at]!;
    next = { ...next, locations: { ...next.locations, [at]: { ...location, lastResolved: next.time } } };
    if (!next.player.knownLocations.includes(at)) {
      // Walked past it: now it's a pin (§16 fog of knowledge).
      next = { ...next, player: { ...next.player, knownLocations: [...next.player.knownLocations, at] } };
      led.notes.push(`found ${location.name}`);
    }
  }
  return done(next, action, minutes, led);
}

// ---------------------------------------------------------------------------
// violence (§17) — a scene, not a mode
// ---------------------------------------------------------------------------

function start(state: WorldState, action: Extract<Action, { type: 'start_scene' }>): StepResult | string {
  const opened = startScene(state, action.demand === undefined ? { kind: action.kind, npc: action.npc } : { kind: action.kind, npc: action.npc, demand: action.demand });
  if (typeof opened === 'string') return opened;
  const led = ledger();
  led.notes.push(`${action.kind} with ${state.npcs[action.npc]?.name ?? action.npc}`);
  return done(opened, action, 0, led);
}

function beat(state: WorldState, action: Extract<Action, { type: 'beat' }>): StepResult | string {
  const resolved = resolveBeat(state, action.verb);
  if (typeof resolved === 'string') return resolved;
  const led = ledger();
  const minutes = TUNING.violence.minutesPerBeat;
  const next = passTime(resolved.state, minutes, led);
  if (resolved.end) {
    led.events.push(resolved.end.event);
    led.notes.push(`${resolved.end.became} ended: ${resolved.end.outcome ?? 'nobody won'}`);
    if (resolved.end.startedAs !== resolved.end.endedAs) led.notes.push(`intent went from ${resolved.end.startedAs} to ${resolved.end.endedAs}`);
  }
  return done(next, action, minutes, led, { beat: { lines: resolved.lines, end: resolved.end } });
}
